<script lang="ts">
  /**
   * Sagt, was fehlt, warum es fehlt und was zu tun ist.
   *
   * ## Zwei Arten, und sie zu verwechseln ist der Fehler
   *
   * Das Reisetagebuch der App zeigte „Noch nichts notiert.", während auf dem
   * Server fünfzehn Einträge lagen. Der Satz war nicht falsch programmiert — er
   * war falsch gedacht: Er beschrieb den Inhalt („nichts da"), obwohl der
   * Zustand ein anderer war („nicht abrufbar").
   *
   * | `art` | Wann | Haltung |
   * |---|---|---|
   * | `anfang` | Es wurde noch nichts angelegt | **einladen** — „Leg die erste an" |
   * | `bedingung` | Die Bedingung ist nicht erfüllt | **erklären** — wann es erscheint |
   *
   * Der dritte Fall — leer, weil nicht gesucht werden konnte — ist **kein**
   * Leerzustand, sondern ein Fehlerzustand. Dafür ist `Hinweis art="ernst"` da.
   *
   * Ein neuer Nutzer sieht in einer frischen Reise zuerst elf leere Panels.
   * Wenn die aussehen wie ein defektes Programm, ist er weg — sie müssen
   * aussehen wie ein unbeschriebenes Blatt. Deshalb: gemeinsame Form, ein
   * Rahmen, immer ein Satz, der sagt, woher der Inhalt kommt.
   *
   * **Kein Emoji — eine Illustration aus dem eigenen Satz schon.** Emoji sehen
   * auf jeder Plattform anders aus und gehören keinem Haus; die Bilder in
   * `gestaltung/Illustration.svelte` sind im selben Strich gezeichnet wie der
   * Zeichensatz. Sie sind freiwillig und stehen an den Flächen, die jemand
   * beim ersten Mal sieht — an einer leeren Zeile mitten in einer Liste wären
   * zehn Bilder untereinander ein Muster und keine Auskunft.
   */
  import type { Snippet } from 'svelte';
  import Illustration, { type Motivname } from '../Illustration.svelte';
  import { istEinladend, type Leerart } from './muster';

  interface Props {
    art?: Leerart;
    titel?: string | null;
    text?: string | null;
    /**
     * Das Bild über dem Titel — klein, aus dem Motivsatz des Hauses.
     *
     * Ohne Angabe steht die Fläche da wie vorher. Es trägt keine Auskunft, die
     * nicht auch im Text steht, und ist deshalb für Vorlesegeräte
     * ausgeblendet.
     */
    bild?: Motivname | null;
    /** Der eine Knopf, der aus dem Leerzustand herausführt. */
    aktion?: Snippet;
    /** Für Leerzustände innerhalb einer Liste statt anstelle einer Fläche. */
    dicht?: boolean;
  }
  let {
    art = 'anfang',
    titel = null,
    text = null,
    bild = null,
    aktion,
    dicht = false
  }: Props = $props();

  const einladend = $derived(istEinladend(art));
</script>

<div class="leer" class:einladend class:dicht>
  {#if bild}<Illustration name={bild} breite={96} />{/if}
  {#if titel}<strong>{titel}</strong>{/if}
  {#if text}<p>{text}</p>{/if}
  {#if aktion}<div class="aktion">{@render aktion()}</div>{/if}
</div>

<style>
  .leer {
    display: grid;
    gap: var(--raum-4);
    max-width: 46ch;
    padding: var(--gruppe);
    border-radius: var(--radius);
    justify-items: start;
    /* `bedingung`: keine Einladung, sondern eine Erklärung — ruhige Fläche
       ohne Rahmen. Hier soll man nichts anlegen, sondern verstehen. */
    border: 0;
    background: var(--surface-2);
  }

  .dicht {
    padding: var(--gruppe-eng);
  }

  /* Ein gestrichelter Rahmen bei „anfang": Er sagt „hier kommt etwas hin"
     statt „hier ist etwas kaputt". */
  .einladend {
    border: 1px dashed var(--border-stark);
    background: transparent;
  }

  strong {
    font-size: var(--fs-panel);
    font-weight: var(--gw-halb);
  }

  p {
    margin: 0;
    font-size: var(--fs-text);
    color: var(--text-dim);
    line-height: var(--zh-normal);
    text-wrap: pretty;
  }

  .aktion {
    margin-top: var(--raum-2);
  }
</style>
